import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Banknote, Download, Layers, Plus, Sparkles } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import {
  EntityDrawer,
  InlineAISuggestion,
  OperationsReportsButton,
  PageHeader,
  WorkflowStateBadge,
} from "../components/ui";
import { HrInitiateLink } from "../components/hr_operations";
import { api, ApiError } from "../lib/api";
import type {
  LoanApplicationDetail,
  LoanInstallmentEntry,
  LoanProductDetail,
} from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";
import { useCurrentEmployeeId } from "./_session3dShell";

// Loan Applications (M16.7). Two-pane: list + selected detail with
// state transitions + repayment schedule + workflow_event history.
// `?employee_id=&le=` for an employee-scoped view; `?le=` alone for HR.

function NewLoanRequestDialog({
  employeeId,
  legalEntityId,
  products,
  onClose,
  onCreated,
}: {
  employeeId: string;
  legalEntityId: string;
  products: LoanProductDetail[];
  onClose: () => void;
  onCreated: (app: LoanApplicationDetail) => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    loan_product_id: products[0]?.id ?? "",
    requested_amount: "10000",
    requested_tenure_months: 12,
    purpose: "",
  });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () =>
      api.createLoanApplication({
        employee_id: employeeId,
        legal_entity_id: legalEntityId,
        loan_product_id: form.loan_product_id,
        requested_amount: form.requested_amount,
        requested_tenure_months: form.requested_tenure_months,
        purpose: form.purpose || null,
      }),
    onSuccess: (app) => onCreated(app),
    onError: (err) =>
      setErrorMsg(err instanceof ApiError ? err.detail : String(err)),
  });
  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("operations.loan_apply_cta")}
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            form="loan-apply-form"
            disabled={m.isPending}
            className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
          >
            {m.isPending
              ? t("operations.loan_submitting")
              : t("operations.loan_submit")}
          </button>
        </div>
      }
    >
      <form
        id="loan-apply-form"
        onSubmit={(e) => {
          e.preventDefault();
          setErrorMsg(null);
          m.mutate();
        }}
        className="flex flex-col gap-3 text-sm"
      >
        <label>
          <span className="text-xs font-medium uppercase text-text-tertiary">
            {t("operations.loan_product")}
          </span>
          <select
            value={form.loan_product_id}
            onChange={(e) =>
              setForm({ ...form, loan_product_id: e.target.value })
            }
            className="mt-1 w-full rounded border border-border-subtle bg-surface-default px-3 py-2"
            required
          >
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name_en} ({t("operations.loan_max_summary", {
                  amount: p.max_amount,
                  currency: p.currency,
                  months: p.max_tenure_months,
                })})
              </option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label>
            <span className="text-xs font-medium uppercase text-text-tertiary">
              {t("operations.loan_amount")}
            </span>
            <input
              value={form.requested_amount}
              onChange={(e) =>
                setForm({ ...form, requested_amount: e.target.value })
              }
              className="mt-1 w-full rounded border border-border-subtle bg-surface-default px-3 py-2"
              required
            />
          </label>
          <label>
            <span className="text-xs font-medium uppercase text-text-tertiary">
              {t("operations.loan_tenure_months")}
            </span>
            <input
              type="number"
              min={1}
              max={360}
              value={form.requested_tenure_months}
              onChange={(e) =>
                setForm({
                  ...form,
                  requested_tenure_months: parseInt(e.target.value, 10),
                })
              }
              className="mt-1 w-full rounded border border-border-subtle bg-surface-default px-3 py-2"
            />
          </label>
        </div>
        <label>
          <span className="text-xs font-medium uppercase text-text-tertiary">
            {t("loan.applications.reason_label", "Reason")}
          </span>
          <textarea
            value={form.purpose}
            onChange={(e) => setForm({ ...form, purpose: e.target.value })}
            rows={2}
            className="mt-1 w-full rounded border border-border-subtle bg-surface-default px-3 py-2"
          />
        </label>
        {errorMsg && (
          <p className="rounded bg-rose-50 p-2 text-rose-700">{errorMsg}</p>
        )}
      </form>
    </EntityDrawer>
  );
}

function InstallmentTable({ installments }: { installments: LoanInstallmentEntry[] }) {
  const { t } = useTranslation();
  if (installments.length === 0) {
    return (
      <p className="text-sm italic text-text-tertiary">
        {t("operations.loan_no_installments")}{" "}
        <span className="not-italic">
          {t(
            "operations.loan_no_installments_hint",
            "— the repayment schedule appears once the loan is disbursed.",
          )}
        </span>
      </p>
    );
  }
  return (
    <table className="w-full text-xs">
      <thead className="text-slate-500">
        <tr className="border-b border-slate-100">
          <th className="px-2 py-1 text-start">#</th>
          <th className="px-2 py-1 text-start">{t("operations.loan_col_due")}</th>
          <th className="px-2 py-1 text-end">{t("operations.loan_amount")}</th>
          <th className="px-2 py-1 text-start">{t("operations.loan_col_status")}</th>
        </tr>
      </thead>
      <tbody>
        {installments.map((inst) => (
          <tr
            key={inst.id}
            data-installment-row=""
            data-id={inst.id}
            className="border-b border-slate-50"
          >
            <td className="px-2 py-1">{inst.sequence_no}</td>
            <td className="px-2 py-1 font-mono">{inst.due_date}</td>
            <td className="px-2 py-1 text-end">
              {inst.amount} {inst.currency}
            </td>
            <td className="px-2 py-1">
              <WorkflowStateBadge state={inst.status} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function HistoryPanel({ appId }: { appId: string }) {
  const q = useQuery({
    queryKey: ["loan-app-history", appId],
    queryFn: () => api.getLoanApplicationHistory(appId),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;
  return (
    <ol className="flex flex-col gap-1">
      {(q.data ?? []).map((e) => (
        <li
          key={e.id}
          className="flex gap-3 rounded border border-slate-200 p-2 text-xs"
        >
          <div className="font-mono text-slate-500">
            {new Date(e.occurred_at).toLocaleString()}
          </div>
          <div className="flex-1">
            <span className="font-semibold">{e.transition}</span>
            {e.from_state && (
              <span className="ms-2 text-slate-500">
                {e.from_state} → {e.to_state}
              </span>
            )}
            {e.reason && <span className="ms-2 text-slate-700">— {e.reason}</span>}
          </div>
        </li>
      ))}
    </ol>
  );
}

function ApplicationDetail({
  app,
  onChanged,
}: {
  app: LoanApplicationDetail;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const installmentsQ = useQuery({
    queryKey: ["loan-installments", app.id],
    queryFn: () => api.getLoanApplicationInstallments(app.id),
  });

  const advanceFns = {
    "run-eligibility": () => api.runLoanEligibility(app.id, { actor: "user_hr" }),
    route: () => api.routeLoanApplication(app.id, { actor: "user_hr" }),
    approve: () =>
      api.approveLoanApplication(app.id, { actor: "user_mgr" }),
    disburse: () =>
      api.disburseLoanApplication(app.id, {
        actor: "user_hr",
        disbursement_date: new Date().toISOString().slice(0, 10),
      }),
  };

  const advanceM = useMutation({
    mutationFn: (step: keyof typeof advanceFns) => advanceFns[step](),
    onSuccess: async () => {
      // Refetch installments + history + applications IN PARALLEL and
      // AWAIT them, so by the time onSuccess resolves the detail panel
      // has the freshly-disbursed schedule on screen. The earlier
      // fire-and-forget invalidate left a ~100-200ms gap where
      // `text=In Repayment` was visible but the InstallmentTable was
      // still empty — Playwright assertions in the UI_LOAN_001
      // scenario ran inside that gap and saw count=0 instead of 12.
      await Promise.all([
        qc.refetchQueries({ queryKey: ["loan-installments", app.id] }),
        qc.refetchQueries({ queryKey: ["loan-app-history", app.id] }),
        qc.refetchQueries({ queryKey: ["loan-applications"] }),
      ]);
      onChanged();
    },
  });

  const cancelM = useMutation({
    mutationFn: () =>
      api.cancelLoanApplication(app.id, { actor: "user_emp" }),
    onSuccess: onChanged,
  });
  // Deny — manager rejects the loan. The API has supported deny
  // for a while but no UI button exposed it; UI_LOAN_013 exercises
  // the manager-denial flow so we surface it here.
  const denyM = useMutation({
    mutationFn: () =>
      api.denyLoanApplication(app.id, {
        actor: "user_mgr",
        reason: "Denied by manager via UI",
      }),
    onSuccess: onChanged,
  });

  const nextStep =
    app.workflow_state === "submitted"
      ? ("run-eligibility" as const)
      : app.workflow_state === "eligibility_check"
      ? ("route" as const)
      : app.workflow_state === "pending_approval"
      ? ("approve" as const)
      : app.workflow_state === "approved"
      ? ("disburse" as const)
      : null;

  const stepLabel = (step: typeof nextStep): string => {
    if (step === "run-eligibility") return t("operations.loan_step_run_eligibility");
    if (step === "route") return t("operations.loan_step_route");
    if (step === "approve") return t("operations.loan_step_approve");
    if (step === "disburse") return t("operations.loan_step_disburse");
    return "";
  };
  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <div>
          <div className="text-xs font-medium uppercase text-slate-500">
            {t("operations.loan_application_id_title", {
              id: app.id.slice(0, 8),
            })}
          </div>
          <div className="mt-1 flex items-center gap-2 text-sm">
            <span className="font-medium">
              {app.requested_amount} {app.currency} · {app.requested_tenure_months}mo
            </span>
            <WorkflowStateBadge state={app.workflow_state} />
          </div>
          {app.monthly_installment && (
            <div className="text-xs text-slate-500">
              {t("operations.loan_monthly_prefix")} {app.monthly_installment} {app.currency}
            </div>
          )}
          {app.eligibility_reason && (
            <div className="mt-1 rounded bg-sky-50 p-2 text-xs text-sky-800">
              {t("operations.loan_eligibility_prefix")} {app.eligibility_reason}
            </div>
          )}
          {app.denial_reason && (
            <div className="mt-1 rounded bg-rose-50 p-2 text-xs text-rose-700">
              {t("operations.loan_denial_prefix")} {app.denial_reason}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          {nextStep && (
            <button
              type="button"
              onClick={() => advanceM.mutate(nextStep)}
              disabled={advanceM.isPending}
              aria-label={stepLabel(nextStep)}
              className="rounded bg-emerald-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {advanceM.isPending
                ? t("operations.loan_working")
                : stepLabel(nextStep)}
            </button>
          )}
          {app.workflow_state === "pending_approval" && (
            <button
              type="button"
              onClick={() => denyM.mutate()}
              disabled={denyM.isPending}
              className="rounded border border-rose-300 bg-surface-default px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
            >
              {denyM.isPending
                ? t("operations.loan_denying", "Denying…")
                : t("operations.loan_deny", "Deny")}
            </button>
          )}
          {["draft", "submitted", "eligibility_check", "pending_approval"].includes(
            app.workflow_state,
          ) && (
            <button
              type="button"
              onClick={() => cancelM.mutate()}
              disabled={cancelM.isPending}
              className="rounded border border-slate-300 px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-50"
            >
              {t("operations.loan_cancel")}
            </button>
          )}
        </div>
      </header>
      <section>
        <a
          href={`/operations/loans/${app.id}`}
          className="inline-block rounded border border-brand-blue px-3 py-1.5 text-xs font-medium text-brand-blue hover:bg-brand-blue hover:text-white"
        >
          {t("operations.loan_open_rich_detail")}
        </a>
      </section>
      <section>
        <h3 className="mb-2 text-xs font-medium uppercase text-slate-500">
          {t("operations.loan_repayment_schedule")}
        </h3>
        {installmentsQ.isLoading ? <Loading /> : <InstallmentTable installments={installmentsQ.data ?? []} />}
      </section>
      <section>
        <h3 className="mb-2 text-xs font-medium uppercase text-slate-500">
          {t("operations.loan_history")}
        </h3>
        <HistoryPanel appId={app.id} />
      </section>
    </div>
  );
}

export function LoanApplicationsPage() {
  const { t } = useTranslation();
  const params = new URLSearchParams(window.location.search);
  // M3X.LIST-AND-REPORT-FIXES PR 1: both employee_id and legal_entity
  // filters are OPTIONAL. Cold load returns the tenant-wide list.
  const employeeId = params.get("employee_id") || undefined;
  // Self-service: when no `?employee_id=` is present, fall back to
  // the current logged-in user (the M16 UI_LOAN_001 Playwright
  // scenario goes through this path).
  const selfEmployeeId = useCurrentEmployeeId();
  const newApplicationTargetId = employeeId ?? (selfEmployeeId || undefined);
  const { legalEntityId: le, gate } = useLegalEntityGate();
  const [showNew, setShowNew] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const qc = useQueryClient();

  const listQ = useQuery({
    queryKey: ["loan-applications", employeeId ?? "all", le ?? "all"],
    queryFn: () =>
      api.listLoanApplications(
        employeeId ? { employeeId } : le ? { legalEntityId: le } : {},
      ),
  });

  const productsQ = useQuery({
    queryKey: ["loan-products", le, false],
    queryFn: () => api.listLoanProducts(le),
    enabled: !!le,
  });

  if (gate) return gate;
  if (listQ.isLoading) return <Loading />;
  if (listQ.error) return <ErrorPanel message={(listQ.error as Error).message} />;

  const apps = listQ.data ?? [];
  const products = productsQ.data ?? [];
  const selected = selectedId ? apps.find((a) => a.id === selectedId) : null;
  // Invalidate by PREFIX so every (employee_id, le) variant of the
  // query is refetched, including the "all-tenant" cold-load shape
  // that uses "all" sentinels in the key. The earlier exact-key
  // invalidate silently missed the cold-load case → new rows never
  // appeared in the table after submit → UI_LOAN_001 capture step
  // timed out on `tr:has-text(reason)`.
  const refresh = () =>
    qc.invalidateQueries({ queryKey: ["loan-applications"] });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("loan.applications_title")}
        breadcrumbs={[
          { label: t("nav.operations"), href: "/operations" },
          { label: t("nav.loans"), href: "/operations/loans/applications" },
          { label: t("loan.applications_breadcrumb") },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <HrInitiateLink transactionType="loan" />
            <OperationsReportsButton
              domain="loans"
              domainLabel={t("nav.loans", "Loans")}
            />
            <button
              type="button"
              onClick={() => setShowBulk(true)}
              disabled={apps.length === 0}
              className="flex items-center gap-1 rounded-sm border border-border-subtle bg-white px-3 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken disabled:opacity-50"
            >
              <Layers size={14} /> {t("loan.applications.cta.bulk", "Bulk actions")}
            </button>
            {newApplicationTargetId ? (
              <button
                type="button"
                onClick={() => setShowNew(true)}
                disabled={products.length === 0}
                className="flex items-center gap-1 rounded-sm bg-brand px-3 py-2 text-sm font-medium text-brand-fg hover:bg-brand-hover disabled:opacity-50"
                aria-label={t(
                  "loan.applications.cta.new",
                  "New Loan Application",
                )}
              >
                <Plus size={14} /> {t(
                  "loan.applications.cta.new",
                  "New Loan Application",
                )}
              </button>
            ) : null}
          </div>
        }
      />

      {/* §4.9 — Article 90 watch nudge linking to the Session 3a register */}
      <InlineAISuggestion
        agent="mutabaa"
        icon={<Sparkles className="h-4 w-4" />}
        severity="info"
        headline={t(
          "loan.applications.suggestion.headline",
          "Mutaba'aa watches Article 90 (1/3 wage-deduction cap) on every disbursement.",
        )}
        body={t(
          "loan.applications.suggestion.body",
          "Use the Active Loans Register to see headroom + disbursement queue. Bulk actions open the multi-select drawer for batch approve / deny / export.",
        )}
        primaryAction={{
          label: t("loan.applications.suggestion.register", "Open Active Loans"),
          onClick: () => window.location.assign("/operations/loans/active-register"),
        }}
        suggestionKey="loan-apps-article-90"
      />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          {apps.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm italic text-text-tertiary">
              {t("operations.loan_no_applications")}{" "}
              <span className="not-italic">
                {t(
                  "operations.loan_no_applications_hint",
                  "— click 'Apply for loan' above to submit an application.",
                )}
              </span>
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase text-text-tertiary">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">
                    {t("loan.applications.col.product", "Product")}
                  </th>
                  <th className="px-3 py-2 text-start font-medium">
                    {t("loan.applications.col.terms", "Amount · Tenure")}
                  </th>
                  <th className="px-3 py-2 text-start font-medium">
                    {t("loan.applications.col.reason", "Reason")}
                  </th>
                  <th className="px-3 py-2 text-start font-medium">
                    {t("loan.applications.col.state", "State")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {apps.map((a) => {
                  const p = products.find((pp) => pp.id === a.loan_product_id);
                  return (
                    <tr
                      key={a.id}
                      data-id={a.id}
                      onClick={() => setSelectedId(a.id)}
                      className={`cursor-pointer border-b border-slate-100 last:border-b-0 transition ${
                        selectedId === a.id
                          ? "bg-slate-50"
                          : "hover:bg-slate-50"
                      }`}
                    >
                      <td className="px-3 py-2 align-middle">
                        <div className="flex items-center gap-2">
                          <Banknote size={14} className="text-emerald-600" />
                          <span className="font-medium">
                            {p?.name_en ?? a.loan_product_id.slice(0, 8)}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 align-middle text-xs text-slate-500">
                        {a.requested_amount} {a.currency} · {a.requested_tenure_months}mo
                      </td>
                      <td className="px-3 py-2 align-middle text-xs text-slate-700">
                        {a.purpose || ""}
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <WorkflowStateBadge state={a.workflow_state} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          {!selected ? (
            <p className="text-sm text-slate-500">
              {t("operations.loan_pick_application_hint")}
            </p>
          ) : (
            <ApplicationDetail app={selected} onChanged={refresh} />
          )}
        </div>
      </div>
      {showNew && newApplicationTargetId && (
        <NewLoanRequestDialog
          employeeId={newApplicationTargetId}
          legalEntityId={le}
          products={products}
          onClose={() => setShowNew(false)}
          onCreated={(app) => {
            setShowNew(false);
            refresh();
            setSelectedId(app.id);
          }}
        />
      )}
      {showBulk && (
        <EntityDrawer
          open
          onOpenChange={(o) => {
            if (!o) setShowBulk(false);
          }}
          title={t("loan.applications.bulk.title", "Bulk loan actions")}
          footer={
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowBulk(false)}
                className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
              >
                {t("common.cancel", "Cancel")}
              </button>
              <button
                type="button"
                onClick={() => {
                  window.alert(
                    t(
                      "loan.applications.bulk.action_note",
                      `Bulk action on ${bulkSelected.size} applications — wiring lands in a focused follow-up. The selection + chrome are the load-bearing surface today.`,
                    ),
                  );
                  setShowBulk(false);
                  setBulkSelected(new Set());
                }}
                disabled={bulkSelected.size === 0}
                className="inline-flex items-center gap-1 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95 disabled:opacity-40"
              >
                <Download className="h-4 w-4" />
                {t("loan.applications.bulk.export", "Export selected")} ({bulkSelected.size})
              </button>
            </div>
          }
        >
          <div className="space-y-3 p-4 text-sm">
            <p className="text-xs text-text-tertiary">
              {t(
                "loan.applications.bulk.intro",
                "Check applications to act on. Future actions: bulk approve eligibility-cleared · bulk deny with reason · bulk export to Excel.",
              )}
            </p>
            <div className="flex items-center gap-2 border-b border-border-subtle pb-2 text-xs">
              <input
                type="checkbox"
                checked={bulkSelected.size === apps.length && apps.length > 0}
                onChange={() => {
                  if (bulkSelected.size === apps.length) setBulkSelected(new Set());
                  else setBulkSelected(new Set(apps.map((a) => a.id)));
                }}
                aria-label={t("loan.applications.bulk.select_all", "Select all")}
              />
              <span className="text-text-secondary">
                {t("loan.applications.bulk.select_all", "Select all")}
              </span>
            </div>
            <ul className="max-h-[400px] space-y-1 overflow-y-auto">
              {apps.map((a) => {
                const p = products.find((pp) => pp.id === a.loan_product_id);
                return (
                  <li key={a.id} className="flex items-start gap-2 rounded border border-border-subtle p-2">
                    <input
                      type="checkbox"
                      checked={bulkSelected.has(a.id)}
                      onChange={() => {
                        const next = new Set(bulkSelected);
                        if (next.has(a.id)) next.delete(a.id);
                        else next.add(a.id);
                        setBulkSelected(next);
                      }}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium">{p?.name_en ?? a.loan_product_id.slice(0, 8)}</span>
                        <WorkflowStateBadge state={a.workflow_state} />
                      </div>
                      <div className="text-xs text-text-tertiary">
                        {a.requested_amount} {a.currency} · {a.requested_tenure_months}mo
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </EntityDrawer>
      )}
    </div>
  );
}
